// Cosmetic, deterministic and bounded. Combat cones/rings remain owned by BossArt.telegraph.
import type { BossSnap } from '../../common/src/protocol';
import { BOSSES } from '../../common/src/data';
import { PALETTE } from './theme';
export type BossFx = { layer:'ground'|'body'; color:string; alpha:number } & (
  {kind:'ring';x:number;y:number;rx:number;ry:number;width:number} |
  {kind:'glow';x:number;y:number;rx:number;ry:number} |
  {kind:'dot';x:number;y:number;size:number} |
  {kind:'line';points:number[];width:number});
const TAU=Math.PI*2;
const clamp=(v:number)=>Math.max(0,Math.min(1,v));
export function bossEffects(b:BossSnap,t:number,lowFx=false):BossFx[]{
  const out:BossFx[]=[], phase=Math.max(0,Math.min(2,b.ph)), pulse=.5+.5*Math.sin(t*4), active=!!b.atk;
  const attacks=BOSSES.tiers[b.k].attacks as Record<string,{windup?:number}>;
  const charge=active&&!b.on?clamp(1-b.at/Math.max(.1,attacks[b.atk]?.windup??1)):b.on?1:0;
  const col=b.k==='brute'?PALETTE.pink:PALETTE.cyan;
  const ring=(layer:'ground'|'body',x:number,y:number,rx:number,ry:number,color:string,alpha:number,width=1)=>out.push({kind:'ring',layer,x,y,rx,ry,color,alpha,width});
  const glow=(x:number,y:number,rx:number,ry:number,color:string,alpha:number)=>out.push({kind:'glow',layer:'body',x,y,rx,ry,color,alpha});
  const dot=(x:number,y:number,size:number,color:string,alpha:number)=>out.push({kind:'dot',layer:'body',x,y,size,color,alpha});
  const line=(points:number[],color:string,alpha:number,width=1)=>out.push({kind:'line',layer:'body',points,color,alpha,width});
  if(b.w>0){
    // Arrival column and descending bracket fragments, underneath authoritative warning marks.
    if(lowFx)return out;
    glow(0,-40,8,58,col,.08);
    ring('ground',0,-2,12+pulse*3,4,col,.25);
    for(let i=0;i<8;i++){const y=-((t*24+i*13)%96);line([-12,y,-12,y+5],col,.12+(1+y/96)*.3);line([12,y,12,y+5],col,.12+(1+y/96)*.3);}
    return out;
  }
  const a=b.a,side=Math.abs(Math.cos(a))>Math.abs(Math.sin(a))*1.1,west=Math.cos(a)<0,dir=west?-1:1,back=!side&&Math.sin(a)<0;
  if(lowFx){
    // A steady emitter marks charge without particles or moving overlays.
    if(active)dot(side?dir*(b.k==='kaiju'?40:12):0,b.k==='kaiju'?-86:b.k==='mech'?-31:-35,2,col,.7);
    return out;
  }
  if(b.k==='brute'){
    const gx=side?-dir*5:-14,gy=b.atk==='smash'&&!b.on?-44:-20;
    glow(gx,gy,7+charge*3,8+charge*3,col,.15+charge*.25);
    if(active){
      for(let i=0;i<4+phase*2;i++){
        const angle=t*(i%2?4:-3)+i*TAU/(4+phase*2),r=5+charge*3;
        const x=gx+Math.cos(angle)*r,y=gy+Math.sin(angle)*r;
        line([x,y,x+Math.sin(angle+.6)*2,y-Math.cos(angle+.6)*2,x+Math.sin(angle)*4,y-Math.cos(angle)*4],i%3?col:PALETTE.offWhite,.3+charge*.45,.65);
      }
    }
    // Sparse embers rise from the cybernetic gauntlet in the later phases.
    for(let i=0;i<phase*3+(active?4:0);i++){const u=(t*.7+i*.19)%1;dot(gx+Math.sin(i*9+t)*5,gy-u*17,1,PALETTE.amber,(1-u)*.7);}
    if(b.atk==='shotgun'&&b.on){glow(side?dir*20:17,side?-26:-18,7,4,PALETTE.amber,.5);}
  }else if(b.k==='mech'){
    const cx=side?-dir*21:0,cy=back?-35:-34;
    glow(cx,cy,11+charge*3,11+charge*3,col,.18+phase*.06);
    ring('body',cx,cy,7+charge,7+charge,col,.2+charge*.12,.5);
    for(let i=0;i<6;i++){const a=t*(1+phase*.4)+i*TAU/6;dot(cx+Math.cos(a)*7,cy+Math.sin(a)*7,1.2,PALETTE.offWhite,.45);}
    // Exhaust is constrained to the hull sides; the ground telegraph is not filled with smoke.
    for(const s of [-1,1])for(let i=0;i<5+phase*2;i++){
      const u=(t*.5+i*.17)%1;dot(s*23+s*u*5,-39-u*16,1+u*2,PALETTE.offWhite,(1-u)*.12);
    }
    if(active){
      for(let i=0;i<6+phase*2;i++){const u=(t*1.5+i*.17)%1;dot((i%2?1:-1)*(15+u*10),-45-u*14,1,col,(1-u)*(.25+charge*.6));}
      if(b.atk==='charge')for(const x of [-15,15])line([x,-6,x,-17,x+Math.sin(t*19)*2,-24],PALETTE.amber,.3+charge*.5);
      if(b.atk==='guns'&&b.on){const y=side?-37:-29;for(const x of side?[dir*56]:[-26,26]){glow(x,y,5+pulse*2,3,PALETTE.amber,.6);dot(x,y,2,PALETTE.offWhite,.9);}}
      if(b.atk==='mortar')for(const x of [-20,20]){glow(x,-47,3+charge*3,2,PALETTE.amber,.2+charge*.4);line([x,-45,x,-55-charge*12],PALETTE.amber,.15+charge*.35);}
    }
  }else{
    const charged=b.atk==='breath',intensity=charged?.4+charge*.6:.2+phase*.1;
    // Dorsal ignition climbs from tail to skull, then a corona gathers at the jaws.
    const ridge=side?[[-19,-38],[-20,-47],[-19,-56],[-16,-65],[-12,-74],[-7,-82],[-2,-91],[3,-97]]:[[-18,-88],[-12,-96],[-6,-104],[0,-107],[6,-104],[12,-96],[18,-88]];
    ridge.forEach(([x,y],i)=>{const gain=charged?clamp(charge*1.6-i/ridge.length*.6):.3+phase*.15;glow(side?x*dir:x,y,3+gain*3,5+gain*5,col,gain*.15);dot(side?x*dir:x,y,1,col,gain*.7);});
    const mx=side?dir*42:0,my=back?-97:side?-96:-84;
    if(charged){
      glow(mx,my,6+charge*10,4+charge*8,col,.15+charge*.25);
      glow(mx,my,2+charge*4,2+charge*3,PALETTE.offWhite,.25+charge*.25);
      for(let i=0;i<12+phase*4;i++){
        const a=i*2.4+t*.7,u=(t*1.1+i*.17)%1,r=(1-u)*(20+phase*4);
        dot(mx+Math.cos(a)*r,my+Math.sin(a)*r,1.1,col,(1-u)*.8);
      }
      if(b.on){
        // A short muzzle plume, not a second damage cone. The ground cone stays authoritative.
        const dx=side?dir:0,dy=side?.12:back?-1:1;
        for(let i=0;i<9;i++){const d=3+i*2.5,spread=1+i*.25;line([mx+dx*d-dy*spread,my+dy*d+dx*spread,mx+dx*d+dy*spread,my+dy*d-dx*spread],i<3?PALETTE.offWhite:col,.8-i*.06,2);}
      }
    }
    for(let i=0;i<6+phase*4;i++){const u=(t*.3+i*.113)%1;dot(Math.sin(i*13)*30,-25-u*70,1,col,(1-u)*intensity*.5);}
    if(b.atk==='tail'&&!b.on)for(let i=0;i<5;i++)dot((side?-dir:1)*(24+i*6),-8-Math.sin(t*5+i)*3,1.5,col,.25+charge*.4);
  }
  return out;
}

export function drawBossEffectsCanvas(g:CanvasRenderingContext2D,fx:BossFx[],layer:'ground'|'body'){
  g.save();
  for(const f of fx){if(f.layer!==layer)continue;g.globalAlpha=f.alpha;g.fillStyle=g.strokeStyle=f.color;
    if(f.kind==='dot')g.fillRect(f.x-f.size/2,f.y-f.size/2,f.size,f.size);
    else if(f.kind==='glow'){for(let i=5;i>0;i--){g.globalAlpha=f.alpha*(1-i/6)*.5;g.beginPath();g.ellipse(f.x,f.y,f.rx*i/5,f.ry*i/5,0,0,TAU);g.fill();}}
    else if(f.kind==='ring'){g.lineWidth=f.width;g.beginPath();g.ellipse(f.x,f.y,f.rx,f.ry,0,0,TAU);g.stroke();}
    else{g.lineWidth=f.width;g.beginPath();g.moveTo(f.points[0],f.points[1]);for(let i=2;i<f.points.length;i+=2)g.lineTo(f.points[i],f.points[i+1]);g.stroke();}
  }
  g.restore();
}
